import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Grid, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Link,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const LandingPage: React.FC = () => {
  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pt: 4, pb: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <img
            src="/logo-nobackground-5000-orange-grad-wheel.png"
            alt="AppConfig² Logo"
            style={{ maxWidth: '320px', marginBottom: '24px' }}
          />
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700, 
              color: '#333',
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Getting Started with AppConfig²
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 700, 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.25rem' }
            }}
          >
            Your complete guide to onboarding and prerequisites
          </Typography>
        </Box>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 4, borderRadius: 2, mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#ff6b00', fontWeight: 600 }}>
                Before You Begin
              </Typography>
              <Typography variant="body1" paragraph>
                AppConfig² requires administrator consent from your Microsoft Entra ID tenant.
                Please ensure you have the following prerequisites before starting:
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <AdminPanelSettingsIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Administrator Account" 
                    secondary="You must have Global Administrator, Cloud Application Administrator, or Application Administrator role in your Entra ID tenant"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Permission to Grant Consent" 
                    secondary="Authority to grant application permissions at the organizational level"
                  />
                </ListItem>
              </List>
            </Paper>

            <Paper sx={{ p: 4, borderRadius: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#ff6b00', fontWeight: 600 }}>
                Onboarding Process
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Step 1: Sign In
                </Typography>
                <Typography variant="body1" paragraph>
                  Sign in with your Microsoft Entra ID account. You'll be directed to Microsoft's authentication page.
                </Typography>
                <Box sx={{ textAlign: 'center', my: 3 }}>
                  <img 
                    src="/docs/images/sign-in-screen.png" 
                    alt="Sign in screen" 
                    style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Step 2: Administrator Consent
                </Typography>
                <Typography variant="body1" paragraph>
                  After signing in, you'll be prompted to grant administrator consent for the required permissions.
                  You must be signed in with an administrator account for this step.
                </Typography>
                <Box sx={{ textAlign: 'center', my: 3 }}>
                  <img 
                    src="/docs/images/admin-consent-screen.png" 
                    alt="Admin consent screen" 
                    style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Step 3: Access the Application
                </Typography>
                <Typography variant="body1" paragraph>
                  Once consent is granted, you'll have full access to all features of AppConfig².
                </Typography>
                <Box sx={{ textAlign: 'center', my: 3 }}>
                  <img 
                    src="/docs/images/app-dashboard.png" 
                    alt="Application dashboard" 
                    style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          {/* Right Column */}
          <Grid item xs={12} md={5}>
            <Card sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#ff6b00', display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1 }} /> Required Permissions
                </Typography>
                <Typography variant="body2" paragraph>
                  AppConfig² requires the following permissions to function properly:
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="User.Read" 
                      secondary="Read your basic profile information"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Directory.Read.All" 
                      secondary="Read directory data to discover applications and users"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Application.ReadWrite.All" 
                      secondary="Manage application registrations and settings"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Policy.Read.All" 
                      secondary="Read authentication policy configurations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Policy.ReadWrite.ApplicationConfiguration" 
                      secondary="Manage claims mapping policies for applications"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Directory.AccessAsUser.All" 
                      secondary="Access directory as the signed-in user"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Policy.Read.ConditionalAccess" 
                      secondary="Read your organization's conditional access policies"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="User.ReadWrite.All" 
                      secondary="Read and write all users' full profiles"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
            
            <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#ff6b00', display: 'flex', alignItems: 'center' }}>
                <HelpOutlineIcon sx={{ mr: 1 }} /> Frequently Asked Questions
              </Typography>
              
              <Accordion elevation={0} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">What if I don't have administrator permissions?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    You'll need to contact your IT administrator or a Global Administrator 
                    in your organization to complete the onboarding process. You can forward 
                    them this guide or our onboarding email to help them understand the requirements.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion elevation={0} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Why does AppConfig² need these permissions?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    These permissions allow AppConfig² to discover and manage your Entra ID 
                    applications, including updating configurations, managing policies, and testing 
                    authentication flows. Without these permissions, many of the key features would 
                    not function properly.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion elevation={0} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Is AppConfig² secure?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Yes, AppConfig² follows Microsoft security best practices. We never store 
                    your credentials, all data is encrypted in transit and at rest, and we use 
                    the most secure authentication methods available through Microsoft's identity 
                    platform.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion elevation={0} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Can I revoke consent later?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Yes, administrators can revoke consent at any time through the Microsoft Entra ID 
                    Admin Center under Enterprise Applications. However, this will prevent AppConfig² 
                    from functioning until consent is granted again.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Paper>
            
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#ff6b00' }}>
                Need Help?
              </Typography>
              <Typography variant="body1" paragraph>
                If you encounter any issues during the onboarding process, please contact our support team:
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" component="div">
                  <strong>Email:</strong> <Link href="mailto:support@appconfig.app">support@appconfig.app</Link>
                </Typography>
                <Typography variant="body2" component="div">
                  <strong>Phone:</strong> +420 xxx xxx xxx
                </Typography>
              </Box>
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth
                href="https://appconfig.app/" 
                target="_blank"
                endIcon={<ArrowForwardIcon />}
                sx={{ mt: 1 }}
              >
                Visit Support Portal
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingPage;